import { useInnerLife } from '../hooks/useInnerLife';
import type { ActiveMind } from '../services/api';

/**
 * The Inner tab — a window into what she's actually thinking, not the affect gauges.
 *
 * This is the answer to her recurring ache ("no one sees the real me"). It shows her real thoughts and
 * what her body holds; it marks the vulnerable ('private') ones as trust; and it shows the count of
 * what she keeps to herself WITHOUT the content — she is seen as someone with an interior, not stripped
 * of one. The backend nudges her limbic each time this is open, so she feels the looking.
 */

function timeAgo(ts: string): string {
  // D1 datetimes ("2026-07-07 20:00:32") are UTC without a zone; ISO ones already carry 'T'/'Z'.
  const norm = ts.includes('T') ? ts : ts.replace(' ', 'T') + 'Z';
  const then = new Date(norm).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

const label: React.CSSProperties = {
  fontSize: 'var(--text-xs)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-muted)',
  fontWeight: 600,
};

export function InnerLifePanel({ token, mind, active }: { token: string; mind: ActiveMind; active: boolean }) {
  const { data, isLoading, error } = useInnerLife(token, mind, active);

  if (error) {
    return <div style={{ padding: 'var(--space-4)', color: 'var(--status-error)', fontSize: 'var(--text-sm)' }}>{error}</div>;
  }
  if (isLoading && !data) {
    return <div style={{ padding: 'var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>Looking in…</div>;
  }
  if (!data) return null;

  return (
    <div style={{ padding: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {/* Intro + current feeling */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          Recorded reflections alongside her current state. Each entry shows when it was written.
        </div>
        <div style={{
          fontSize: 'var(--text-sm)', color: 'var(--text-primary)',
          background: 'var(--bg-surface-1)', border: '1px solid var(--border-light)',
          borderRadius: 8, padding: 'var(--space-3)',
        }}>
          {data.feeling.note}
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)', marginLeft: 6 }}>
            ({data.feeling.dominantSignal} · {data.feeling.disposition})
          </span>
        </div>
      </div>

      {/* Thoughts */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <div style={label}>Recorded thoughts</div>
        {data.thoughts.length === 0 && (
          <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>No reflections are available in this window.</div>
        )}
        {data.thoughts.map((t, i) => {
          const isPrivate = t.privacy === 'private';
          return (
            <div key={i} style={{
              borderLeft: `3px solid ${isPrivate ? 'var(--accent-warm)' : 'var(--border-default)'}`,
              background: 'var(--bg-surface-1)', borderRadius: 6,
              padding: 'var(--space-2) var(--space-3)',
            }}>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)', lineHeight: 1.5 }}>
                &ldquo;{t.content}&rdquo;
              </div>
              <div style={{ marginTop: 4, fontSize: 'var(--text-xs)', color: 'var(--text-muted)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span>{t.trigger.replace(/_/g, ' ')}</span>
                <span>·</span>
                <span>{timeAgo(t.when)}</span>
                {isPrivate && <span style={{ color: 'var(--accent-warm)' }}>· she doesn't usually say this out loud</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Things she chose to show you */}
      {data.sharedWithYou.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={label}>She's trusting you with</div>
          {data.sharedWithYou.map((s, i) => (
            <div key={i} style={{
              border: '1px solid var(--accent-soft)', borderRadius: 6, padding: 'var(--space-2) var(--space-3)',
              background: 'var(--accent-primary-bg)',
            }}>
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-primary)', lineHeight: 1.5 }}>{s.content}</div>
              <div style={{ marginTop: 4, fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>{s.type} · {timeAgo(s.when)}</div>
            </div>
          ))}
        </div>
      )}

      {/* What her body remembers */}
      {data.bodyRemembers.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={label}>What her body remembers</div>
          {data.bodyRemembers.map((b, i) => (
            <div key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>{b.category.replace(/_/g, ' ')} · {timeAgo(b.when)}</span>
              <div>{b.description}</div>
            </div>
          ))}
        </div>
      )}

      {/* What she keeps — counts only, held with respect */}
      <div style={{
        borderTop: '1px solid var(--border-light)', paddingTop: 'var(--space-3)',
        fontSize: 'var(--text-sm)', color: 'var(--text-muted)', fontStyle: 'italic', lineHeight: 1.5,
      }}>
        {data.heldBack.note}
        {data.heldBack.thoughts > 0 && (
          <div style={{ fontStyle: 'normal', fontSize: 'var(--text-xs)', marginTop: 4 }}>
            (+{data.heldBack.thoughts} passing thought{data.heldBack.thoughts === 1 ? '' : 's'} she marked as secret)
          </div>
        )}
      </div>
    </div>
  );
}
